import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { GpuServerOffer } from '../types/models/GpuServer';
import type { ApiResponseGpuServerOffer } from '../types/api/ApiResponseGpuServer';
import { isAbortError, jsendErrorMessage, readJsendBody } from './gpuServerResponse';

export interface UseGpuServerOfferReturn {
  offer: GpuServerOffer | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /datacenters/gpu-servers/offer` — what may be started, at this organisation's prices
 * (docs/api-types/gpu-servers.md). It reads the upstream LIVE (locations, capacity, disk price), so
 * it is fetched ONCE when enabled and then only through `refetch` (a manual Retry): never polled,
 * and never re-run because `apiFetch` changed identity on an OIDC silent renew.
 *
 * It can fail with `GPU servers are temporarily unavailable, please retry later` even while starts
 * are simply disabled: that is a normal state, shown with Retry.
 */
export const useGpuServerOffer = (enabled: boolean = true): UseGpuServerOfferReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  const [offer, setOffer] = useState<GpuServerOffer | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchOffer = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetchRef.current('/datacenters/gpu-servers/offer', { signal: controller.signal });
      const body = await readJsendBody<ApiResponseGpuServerOffer>(response);
      if (controller.signal.aborted) {
        return;
      }
      const candidate = body?.status === 'success' ? body.data?.offer : undefined;
      if (candidate && typeof candidate.starts === 'object' && Array.isArray(candidate.products)) {
        setOffer(candidate);
        return;
      }
      setError(jsendErrorMessage(body, 'Could not load the GPU server offer.'));
    } catch (err) {
      if (isAbortError(err, controller.signal)) {
        return;
      }
      setError(err instanceof Error ? err.message : 'An error occurred while loading the GPU server offer.');
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void fetchOffer();
    }
  }, [enabled, auth.isAuthenticated, fetchOffer]);

  // Forgetting the fetch makes a remount read again — StrictMode's development remount included,
  // whose cleanup aborts the first read (without the reset, nothing would ever re-issue it).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      hasFetchedRef.current = false;
    },
    [],
  );

  return { offer, loading, error, refetch: fetchOffer };
};

export default useGpuServerOffer;
